import React, { useEffect, useMemo, useState } from 'react'

// Components
import InsightsMatrix, {
  IMemberPlot,
  IZoneCount,
  ViewMode,
  scoreToZone,
} from './InsightsMatrix'

// Styles
import { sizes } from '../../styles/mediaQueries'

// Utils
import { useAppState } from '../../context/AppContext'
import getInsightsStats from '../../context/services/insights/getInsights.service'

// Types
import { ICompanyMember, ICompanyTerm } from '../../common/models/Company'

interface IMemberSnapshot {
  memberId: string
  cultureAverage: number
  skillAverage: number
  cultureAssessed: boolean
  skillAssessed: boolean
}

interface IProps {
  selectedTerm: ICompanyTerm
  compareTo?: ICompanyTerm
  activeMemberIds: string[]
  view: ViewMode
  setMemberForModal?: (member: ICompanyMember) => void
}

const Insights: React.FC<IProps> = ({
  selectedTerm,
  compareTo,
  activeMemberIds,
  view,
  setMemberForModal,
}) => {
  const { company, userAuthToken } = useAppState()

  const [membersSnapshot, setMembersSnapshot] = useState<IMemberSnapshot[]>([])
  const [prevMembersSnapshot, setPrevMembersSnapshot] = useState<
    IMemberSnapshot[]
  >([])
  const [isMobile, setIsMobile] = useState<boolean>(false)

  useEffect(() => {
    if (!window) return
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < sizes.M)
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const loadSnapshot = async (
    termNumber: number
  ): Promise<IMemberSnapshot[]> => {
    if (!userAuthToken) return []
    const stats = await getInsightsStats(userAuthToken, termNumber)
    return stats.memberStatistics.map((stat) => ({
      memberId: stat.memberId,
      // Members not assessed on a type are plotted at the 50 midpoint so their
      // dot stays visible, but the *Assessed flags let the UI mark that axis as
      // "not assessed" (dashed axis, "-" instead of a score) rather than 50.
      cultureAverage:
        typeof stat.avgCultureResult === 'number' ? stat.avgCultureResult : 50,
      skillAverage:
        typeof stat.avgSkillResult === 'number' ? stat.avgSkillResult : 50,
      cultureAssessed: typeof stat.avgCultureResult === 'number',
      skillAssessed: typeof stat.avgSkillResult === 'number',
    }))
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ;(async () => {
      if (!userAuthToken || !company || !selectedTerm) return
      try {
        setMembersSnapshot(await loadSnapshot(selectedTerm.termNumber))
      } catch {
        setMembersSnapshot([])
      }
    })()
  }, [userAuthToken, company, selectedTerm])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ;(async () => {
      if (!compareTo) {
        setPrevMembersSnapshot([])
        return
      }
      if (!userAuthToken || !company) return
      try {
        setPrevMembersSnapshot(await loadSnapshot(compareTo.termNumber))
      } catch {
        setPrevMembersSnapshot([])
      }
    })()
  }, [userAuthToken, company, compareTo])

  const {
    memberPlots,
    memberPlotsPrev,
    zoneCounts,
    zoneCountsPrev,
    members,
  } = useMemo(() => {
    const activeIdSet = new Set(activeMemberIds)
    const plots: IMemberPlot[] = []
    const plotsPrev: IMemberPlot[] = []
    const includedIds = new Set<string>()
    const zoneMap = new Map<string, IZoneCount>()
    const zoneMapPrev = new Map<string, IZoneCount>()

    for (const s of membersSnapshot) {
      if (!activeIdSet.has(s.memberId)) continue
      includedIds.add(s.memberId)
      plots.push({
        memberId: s.memberId,
        x: s.cultureAverage,
        y: 100 - s.skillAverage,
        cultureAssessed: s.cultureAssessed,
        skillAssessed: s.skillAssessed,
      })
      const zoneX = scoreToZone(s.cultureAverage)
      const zoneY = scoreToZone(s.skillAverage)
      const key = `${zoneX}_${zoneY}`
      const existing = zoneMap.get(key)
      if (existing) {
        existing.memberIds.push(s.memberId)
      } else {
        zoneMap.set(key, { zoneX, zoneY, memberIds: [s.memberId] })
      }
    }

    for (const s of prevMembersSnapshot) {
      if (!activeIdSet.has(s.memberId)) continue
      plotsPrev.push({
        memberId: s.memberId,
        x: s.cultureAverage,
        y: 100 - s.skillAverage,
        cultureAssessed: s.cultureAssessed,
        skillAssessed: s.skillAssessed,
      })
      const zoneX = scoreToZone(s.cultureAverage)
      const zoneY = scoreToZone(s.skillAverage)
      const key = `${zoneX}_${zoneY}`
      const existing = zoneMapPrev.get(key)
      if (existing) {
        existing.memberIds.push(s.memberId)
      } else {
        zoneMapPrev.set(key, { zoneX, zoneY, memberIds: [s.memberId] })
      }
    }

    const matchedMembers: ICompanyMember[] = company
      ? company.members.filter((m) => includedIds.has(m._id))
      : []

    return {
      memberPlots: plots,
      memberPlotsPrev: plotsPrev,
      zoneCounts: Array.from(zoneMap.values()),
      zoneCountsPrev: Array.from(zoneMapPrev.values()),
      members: matchedMembers,
    }
  }, [membersSnapshot, prevMembersSnapshot, activeMemberIds, company])

  return (
    <InsightsMatrix
      zoneCounts={zoneCounts}
      zoneCountsPrev={compareTo ? zoneCountsPrev : undefined}
      memberPlots={memberPlots}
      memberPlotsPrev={compareTo ? memberPlotsPrev : undefined}
      members={members}
      selectedTerm={selectedTerm}
      compareTo={compareTo}
      // The detailed scatter view relies on hover/precise pointer interaction,
      // so on mobile we always fall back to the simplified heatmap 9-grid.
      view={isMobile ? 'heatmap' : view}
      setMemberForModal={setMemberForModal}
    />
  )
}

export default Insights
